import React, { useEffect, useState } from 'react';
import { Mail, Save, Send, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { adminApi } from './AdminAuthContext';

const SECURE_OPTIONS = [
  { id: 'starttls', label: 'STARTTLS (Port 587, empfohlen)' },
  { id: 'ssl',      label: 'SSL/TLS (Port 465)' },
  { id: 'none',     label: 'Unverschlüsselt (nicht empfohlen)' },
];

const EMPTY = {
  smtp_host: '', smtp_port: 587, smtp_user: '',
  smtp_password: '', smtp_from: '', smtp_secure: 'starttls', notify_to: '',
};

export default function AdminSettingsEmailPage() {
  const [cfg, setCfg] = useState(EMPTY);
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Test-email modal
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testStatus, setTestStatus] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get('/admin/settings/email');
        setCfg({
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
          smtp_user: data.smtp_user || '',
          smtp_password: '', // never returned from server
          smtp_from: data.smtp_from || '',
          smtp_secure: data.smtp_secure || 'starttls',
          notify_to: data.notify_to || '',
        });
        setHasPassword(!!data.has_password);
      } catch (e) { setError(e?.response?.data?.detail || 'Konnte Konfiguration nicht laden.'); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setError(null); setSaved(false); setBusy(true);
    try {
      const body = { ...cfg };
      if (!body.smtp_password) delete body.smtp_password; // keep existing
      const { data } = await adminApi.put('/admin/settings/email', body);
      setHasPassword(!!data.has_password);
      setCfg((c) => ({ ...c, smtp_password: '' }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo) { setTestStatus({ ok: false, msg: 'Empfänger fehlt.' }); return; }
    setTestStatus({ pending: true });
    try {
      await adminApi.post('/admin/settings/email/test', { to: testTo });
      setTestStatus({ ok: true, msg: 'Testnachricht versendet.' });
    } catch (err) {
      setTestStatus({ ok: false, msg: err?.response?.data?.detail || 'Fehler.' });
    }
  };

  const field = (key, label, props = {}) => (
    <label key={key} className="block">
      <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">{label}</span>
      <input value={cfg[key] ?? ''} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
        data-testid={`admin-email-${key.replace('smtp_', '')}`}
        className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]"
        {...props} />
    </label>
  );

  return (
    <div data-testid="admin-settings-email-page" className="w-full max-w-none">
      <div className="flex items-center gap-3 mb-6">
        <span className="inline-flex w-10 h-10 rounded-full bg-[#F26B1A]/10 text-[#F26B1A] items-center justify-center">
          <Mail className="w-5 h-5" />
        </span>
        <h1 className="font-display text-[22px] sm:text-[24px] lg:text-[28px] text-[#1F1D1A] leading-none">E-Mail-Versand</h1>
      </div>
      <p className="text-[14px] text-[#5C5852] mb-6 max-w-3xl">
        E-Mail-Konfiguration fuer Lead-Benachrichtigungen. In der Profihost-Standalone-Version wird der Versand ueber PHP mail() ausgeloest.
      </p>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#5C5852]" /> : (
        <form onSubmit={save} className="bg-white rounded-2xl border border-[#E5E0D2] p-6 xl:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4">
            {field('smtp_host', 'SMTP-Server', { placeholder: 'mail.profihost.com', required: true })}
            <label className="block">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Port</span>
              <input type="number" min={1} max={65535} value={cfg.smtp_port}
                onChange={(e) => setCfg({ ...cfg, smtp_port: Number(e.target.value) || 0 })}
                data-testid="admin-email-port" required
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] tabular-nums" />
            </label>
            <label className="block sm:col-span-2 2xl:col-span-2">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Verschlüsselung</span>
              <select value={cfg.smtp_secure} onChange={(e) => setCfg({ ...cfg, smtp_secure: e.target.value })}
                data-testid="admin-email-secure"
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px] bg-white">
                {SECURE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </label>
            {field('smtp_user', 'SMTP-Benutzer', { placeholder: 'info@horrenmaker.nl', required: true })}
            <label className="block">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">SMTP-Passwort</span>
              <input type="password" value={cfg.smtp_password} onChange={(e) => setCfg({ ...cfg, smtp_password: e.target.value })}
                data-testid="admin-email-password"
                placeholder={hasPassword ? '••••••••  (leer lassen = behalten)' : 'Passwort eingeben'}
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
            </label>
            {field('smtp_from', 'Absender-Adresse', { type: 'email', placeholder: 'info@horrenmaker.nl', required: true })}
            {field('notify_to', 'Lead-Benachrichtigung an', { type: 'email', placeholder: 'info@horrenmaker.nl', required: true })}
          </div>

          {error && <p data-testid="admin-email-err" className="inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" />{error}</p>}

          <div className="flex items-center gap-3 pt-2 flex-wrap">
            <button type="submit" disabled={busy} data-testid="admin-email-save"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
            </button>
            <button type="button" onClick={() => { setTestStatus(null); setTestOpen(true); }} data-testid="admin-email-test-open"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-[#E5E0D2] text-[#1F1D1A] text-[13px] font-semibold hover:border-[#F26B1A] transition-colors">
              <Send className="w-4 h-4" /> Testnachricht senden
            </button>
            {saved && <span className="inline-flex items-center gap-1.5 text-[#7FC242] text-[12.5px]"><CheckCircle2 className="w-4 h-4" /> Gespeichert.</span>}
          </div>
        </form>
      )}

      {testOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setTestOpen(false)}>
          <div className="bg-white w-full max-w-sm rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-email-test-modal">
            <p className="font-display text-[18px] mb-3">SMTP-Test</p>
            <label className="block mb-3">
              <span className="block text-[11.5px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1.5">An</span>
              <input type="email" autoFocus value={testTo} onChange={(e) => setTestTo(e.target.value)}
                data-testid="admin-email-test-to"
                className="w-full px-3 py-2 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
            </label>
            {testStatus && (
              <p data-testid="admin-email-test-status" className={`text-[12.5px] mb-3 ${testStatus.ok ? 'text-[#7FC242]' : testStatus.pending ? 'text-[#5C5852]' : 'text-[#C03A1A]'}`}>
                {testStatus.pending ? 'Sendet …' : testStatus.msg}
              </p>
            )}
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setTestOpen(false)}
                className="px-3 py-1.5 rounded-full text-[12px] text-[#5C5852]">Schließen</button>
              <button type="button" onClick={sendTest} data-testid="admin-email-test-send"
                className="px-4 py-1.5 rounded-full bg-[#F26B1A] text-white text-[12px] font-semibold">Senden</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
